import React from "react"
import PropTypes from "prop-types"
import { Divider, Flex, Switch, Tooltip } from "antd"
import { InfoCircleOutlined } from "@ant-design/icons"
import { useTranslation } from "react-i18next"
import MultiSelectWithSearch from "components/common/MultiSelectWithSearch"
import { ResourceCard } from "../ResourceCard"
import { CrewMemberTable } from "../CrewMemberTable"
import styles from "../../index.module.css"
import { Text } from "ui/Typography"

export const CrewMemberSection = ({
  crewMemberData,
  selectedCrewMembers,
  onCrewMemberChange,
  selectedCrewMembersData,
  onEdit,
  onRemove,
  onAddClick
}) => {
  const { t } = useTranslation()

  const crewMemberOptions = [
    { value: "all", label: "Select All" },
    ...(crewMemberData || []).map((member) => ({
      value: String(member.id),
      label: member.name || `${member.first_name || ""} ${member.last_name || ""}`.trim()
    }))
  ]

  const hasSelection = (selectedCrewMembers || []).length > 0

  return (
    <ResourceCard
      title={t("listings.tabs.resources.select_staff_member")}
      buttonText={t("listings.tabs.resources.add_new_staff_member")}
      onAddClick={onAddClick}
    >
      <div className="mt-l">
        <Text fw={500}>
          {t("listings.tabs.resources.select_staff_members", { defaultValue: "Select Crew Members" })}
          <span className={styles.requiredMark}>*</span>
        </Text>
        <MultiSelectWithSearch
          value={selectedCrewMembers || []}
          onChange={onCrewMemberChange}
          options={crewMemberOptions}
          placeholder={t("listings.tabs.resources.select_crew_placeholder", { defaultValue: "Select crew members to add" })}
          searchPlaceholder={t("staff.search_staff") || "Search Staff"}
          style={{ minWidth: "100%", maxWidth: "100%" }}
          t={t}
        />
        {!hasSelection && (
          <Text size="caption" className={styles.emptyExperiencesText}>
            {t("crew_member.no_staff_assigned", { defaultValue: "No crew member assigned yet." })}
          </Text>
        )}
      </div>

      {hasSelection && <CrewMemberTable data={selectedCrewMembersData} onEdit={onEdit} onRemove={onRemove} />}

      <Divider className="my-l" />

      <Text fw={500}>{t("listings.tabs.resources.booking_rules", { defaultValue: "Booking Rules" })}</Text>
      <Flex align="center" className="mt-s" gap={8} wrap>
        <Switch disabled />
        <Text>{t("listings.tabs.resources.auto_assign_staff_to_bookings", { defaultValue: "Auto-assign Staff to bookings" })}</Text>
        <Tooltip
          title={
            <div>
              <p>{t("listings.tabs.resources.auto_assign_staff_to_bookings_tooltip_on_description")}</p>
              <p>{t("listings.tabs.resources.auto_assign_staff_to_bookings_tooltip_off_description")}</p>
            </div>
          }
        >
          <InfoCircleOutlined className={styles.infoIcon} />
        </Tooltip>
      </Flex>
    </ResourceCard>
  )
}

CrewMemberSection.propTypes = {
  crewMemberData: PropTypes.array.isRequired,
  selectedCrewMembers: PropTypes.array.isRequired,
  onCrewMemberChange: PropTypes.func.isRequired,
  selectedCrewMembersData: PropTypes.array.isRequired,
  onEdit: PropTypes.func.isRequired,
  onRemove: PropTypes.func.isRequired,
  onAddClick: PropTypes.func.isRequired
}
